/**
 * The claim endpoint's failure vocabulary, and the customer-facing copy for it.
 *
 * LEAF MODULE — its only import is `lib/dates.ts`, which is itself
 * import-free. `lib/draft-projects/claim.ts` pulls in the Prisma client, so a
 * client component can never import it; this file is the shared half both
 * sides may hold. `claim.ts` derives its result type from `ClaimFailureReason`
 * here, so the union has exactly one definition.
 *
 * WHY THE COPY LIVES HERE. The claim endpoint has TWO doors:
 *   - `components/funnel/claim-callback-client.tsx` — the post-signup callback;
 *   - `components/funnel/merge-draft.tsx` — the signed-in "you have an unfinished
 *     draft" prompt on /start-project/save.
 * A signed-in Starter user at their project limit arrives through the SECOND
 * one, which is exactly the cohort issue #2103 is about. When the copy lived in
 * the callback component only, that user was shown the raw enum token
 * (`project_limit_reached`) in red. `CLAIM_FAILURE_MESSAGES` below is a
 * `Record` over the full union, so a new reason added to `claim.ts` fails
 * typecheck until it declares copy — neither door can regress to a raw token.
 */

import { ukDateTime } from "@/lib/dates";

/** Every failure reason `claimDraft` can return. */
export type ClaimFailureReason =
  | "draft_not_found"
  | "draft_expired"
  | "draft_claimed_by_other_user"
  | "draft_claim_in_progress"
  | "draft_pcpp_processing"
  | "project_limit_reached"
  | "account_read_only"
  | "claim_forbidden"
  | "claim_unavailable";

/**
 * The reasons that are terminal until something about the ACCOUNT changes.
 * Retrying these cannot succeed, so they get their own screen with a real next
 * step rather than the generic error screen's "Try again".
 */
export type BlockedClaimReason =
  | "project_limit_reached"
  | "account_read_only"
  | "claim_forbidden";

export interface BlockedClaimCopy {
  heading: string;
  body: string;
  cta: string;
  ctaHref: string;
}

/**
 * How long the draft really stays recoverable — a DATE the server computed, or
 * null when it could not.
 *
 * NEVER a number of days counted from now. Two independent clocks govern
 * getting back to a draft, both started at draft CREATION and neither
 * refreshed by a denial: the encrypted cookie that is the only way back
 * (`requireMatchingDraftCookie`), which `encryptDraftSession` stamps with a
 * 7-day `exp`, and the DraftProject row's own `expiresAt` (`DRAFT_TTL_DAYS`,
 * 30). The claim route sends the EARLIER of the two as `recoverUntil`. Saying
 * "within 7 days" on the denial screen restarted a clock that does not
 * restart — a visitor denied on day six of the cookie's life has one day.
 */
export type ClaimRecoveryWindow = Date | null;

/**
 * THE rule for "until when is this draft really reachable?", in one place.
 *
 * Two clocks govern getting back to a draft and BOTH must still be running:
 * the DraftProject row's own `expiresAt` (`DRAFT_TTL_DAYS`, 30) and the
 * encrypted `tsb_draft` cookie's JWE `exp` (`draftCookieMaxAgeDays()`, 7 by
 * default) — the cookie being the only credential that can present the draft
 * again. Both start at draft CREATION and neither is refreshed, so the honest
 * answer is the EARLIER of the two.
 *
 * If either is unknown the answer is `null`, never the other one: answering a
 * question about the cookie with the row's much later date is precisely the
 * over-promise this rule exists to prevent, and callers render "no window"
 * copy for `null` rather than inventing one.
 *
 * Shared by the claim route's refusal body (`recoverUntil`) and the
 * authenticated app's resume affordance (issue #2118), so the two surfaces
 * cannot quote different deadlines for the same draft.
 */
export function draftRecoveryDeadline(
  rowExpiresAt: Date | null | undefined,
  cookieExpiresAt: Date | null | undefined,
): ClaimRecoveryWindow {
  if (!rowExpiresAt || !cookieExpiresAt) return null;
  return new Date(
    Math.min(rowExpiresAt.getTime(), cookieExpiresAt.getTime()),
  );
}

/**
 * The deadline as customer-facing text, or `null` when there is no real
 * deadline to quote. Same pinned en-GB / Europe/London formatter, and same
 * date-AND-time precision, as the refusal screens — see
 * `formatRecoveryDeadline`.
 */
export function formatDraftRecoveryDeadline(
  recoverUntil: ClaimRecoveryWindow,
): string | null {
  return recoverUntil ? formatRecoveryDeadline(recoverUntil) : null;
}

/**
 * The deadline, rendered through the pinned en-GB / Europe/London formatter.
 *
 * NOT a bare `toLocaleDateString`: production renders on a UTC server while
 * the customer's browser runs Europe/London, so an unpinned formatter prints
 * different text on each side — a hydration mismatch, and for a deadline
 * falling near midnight during BST, literally the WRONG DAY on the one screen
 * whose entire job is to state a date the customer can rely on.
 * (`__tests__/guardrails/pinned-date-formatting.test.ts` enforces this;
 * `lib/dates.ts` has no imports, so the leaf-module rule above still holds.)
 */
function formatRecoveryDeadline(recoverUntil: Date): string {
  // Date AND TIME. `recoverUntil` is an instant, not a calendar day: the draft
  // cookie is stamped 7 days from the moment of creation, so a draft made at
  // 14:00 dies at 14:00. Rendering the bare day promised up to another ~24
  // hours that do not exist — a customer returning that afternoon would find
  // the cookie gone and the draft unreachable, on the strength of a date this
  // screen named.
  return ukDateTime(recoverUntil);
}

/**
 * The one sentence about the draft's fate, appended to every blocked message.
 * With a real deadline it names the date; without one it promises nothing it
 * cannot back — it never invents a window.
 */
function draftSurvivalSentence(recoverUntil: ClaimRecoveryWindow): string {
  if (recoverUntil) {
    return `Your draft is saved until ${formatRecoveryDeadline(recoverUntil)}.`;
  }
  // NO deadline available — so state only what is already true (this refusal
  // did not consume the draft) and promise NOTHING about coming back. The
  // previous wording, "come back to this page to finish setting it up", was a
  // forward promise made with no idea whether it could be kept: the route
  // reaches here only when the cookie's own expiry is unknown, and that cookie
  // is the sole way back.
  return "Nothing was lost — this did not use up your draft.";
}

/**
 * The call to action, in two versions.
 *
 * "…and it will be waiting here" is a FORWARD PROMISE, so it may only be said
 * when a real deadline backs it. Without one, the wording drops to the action
 * alone. Keeping this beside `draftSurvivalSentence` matters: the survival
 * sentence was made conditional first, and this clause quietly went on
 * promising a comeback in the same paragraph.
 */
function comeback(
  recoverUntil: ClaimRecoveryWindow,
  withDeadline: string,
  withoutDeadline: string,
): string {
  return recoverUntil ? withDeadline : withoutDeadline;
}

const BLOCKED_CLAIM_COPY: Record<
  BlockedClaimReason,
  (recoverUntil: ClaimRecoveryWindow) => BlockedClaimCopy
> = {
  project_limit_reached: (recoverUntil) => ({
    heading: "Starter includes one job",
    body: `Starter includes one job, and this account already has it. ${draftSurvivalSentence(
      recoverUntil,
    )} ${comeback(recoverUntil, "Upgrade and it will be waiting here, ready to open.", "Upgrade to start another.")}`,
    cta: "See plans",
    ctaHref: "/pricing",
  }),
  account_read_only: (recoverUntil) => ({
    heading: "Your account is read-only",
    body: `This account's subscription has been cancelled, so new jobs cannot be created. ${draftSurvivalSentence(
      recoverUntil,
    )} ${comeback(recoverUntil, "Reactivate and it will be waiting here.", "Reactivate to start new jobs.")}`,
    cta: "Reactivate",
    ctaHref: "/pricing",
  }),
  claim_forbidden: (recoverUntil) => ({
    // Role, not plan — an owner or admin has to start the job. Sending this
    // person to /pricing would be worse than useless: their account may be paid
    // already, and buying more of it changes nothing.
    heading: "Your role cannot start new jobs",
    body: `Only an account owner or admin can start a job, and this sign-in is not being allowed to. If you are on someone else’s account as a team member, ask an owner or admin to set the job up. ${draftSurvivalSentence(
      recoverUntil,
    )}`,
    cta: "Go to my dashboard",
    ctaHref: "/dashboard",
  }),
};

/**
 * The blocked-screen copy for an error string, or `null` when the error is not
 * one of the account-state denials (i.e. it belongs on the retryable screen).
 *
 * Accepts `unknown` because the input is a parsed JSON body.
 */
export function blockedClaimCopy(
  error: unknown,
  recoverUntil: ClaimRecoveryWindow = null,
): BlockedClaimCopy | null {
  if (typeof error !== "string") return null;
  // The claim ROUTE denies a SITE_SUPERVISOR with a plain "Forbidden" before
  // `claimDraft` runs, so it never carries a reason code. Same situation for the
  // customer as `claim_forbidden` — a role that may not create projects — so it
  // gets the same screen rather than a dead-end retry. (It also carries no
  // `recoverUntil`, so the copy falls back to the no-window sentence.)
  if (error === "Forbidden") {
    return BLOCKED_CLAIM_COPY.claim_forbidden(recoverUntil);
  }
  return error in BLOCKED_CLAIM_COPY
    ? BLOCKED_CLAIM_COPY[error as BlockedClaimReason](recoverUntil)
    : null;
}

/**
 * Parse the route's `recoverUntil` (an ISO string) into a usable deadline.
 * Anything malformed, absent, or already past becomes `null` — the copy then
 * promises no window rather than a wrong one.
 */
export function parseRecoveryWindow(value: unknown): ClaimRecoveryWindow {
  if (typeof value !== "string") return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.getTime() > Date.now() ? parsed : null;
}

/**
 * One sentence per failure reason, for the retryable error screen.
 *
 * A `Record` over the WHOLE union on purpose: adding a reason to
 * `ClaimFailureReason` breaks the build here until copy exists for it. The
 * blocked reasons carry their blocked-screen body so a caller that renders a
 * plain string (rather than the full screen) still says something true.
 */
const CLAIM_FAILURE_MESSAGES: Record<ClaimFailureReason, string> = {
  draft_not_found:
    "We could not find that draft. Start again and we will build a fresh project.",
  draft_expired:
    "This draft has expired. Start again and we will build a fresh project.",
  draft_claimed_by_other_user:
    "This draft has already been saved to another account.",
  draft_claim_in_progress:
    "This draft is already being saved. Give it a moment and try again.",
  draft_pcpp_processing:
    "The PCCP is still being checked. Give it another moment and try again.",
  // Deliberately NOT a `BlockedClaimReason`, so it lands on the RETRYABLE
  // error screen. The project-creation slot answers "forbidden" from three
  // places, and only one of them is about a role (issue #2117 split them with
  // `ProjectCreationForbiddenReason`). The other two — the account/membership
  // not resolving under the locks, and an ownership change mid-statement — are
  // races that clear on a retry. Reporting them through `claim_forbidden`
  // would tell an account OWNER that their role cannot start jobs and send
  // them to the dashboard instead of the retry that works.
  claim_unavailable:
    "We could not set your job up just now. Nothing was lost — give it a moment and try again.",
  // The blocked bodies WITHOUT a deadline: a caller that renders a plain
  // string has no `recoverUntil` to pass, so it gets the no-window wording
  // rather than a date it cannot substantiate.
  project_limit_reached: BLOCKED_CLAIM_COPY.project_limit_reached(null).body,
  account_read_only: BLOCKED_CLAIM_COPY.account_read_only(null).body,
  claim_forbidden: BLOCKED_CLAIM_COPY.claim_forbidden(null).body,
};

/** Errors raised by the claim ROUTE rather than by `claimDraft` itself. */
const CLAIM_ROUTE_MESSAGES: Record<string, string> = {
  draft_cookie_missing:
    "This browser could not match the saved draft. Go back to the save step and try again.",
  draft_cookie_mismatch:
    "This browser could not match the saved draft. Go back to the save step and try again.",
  Unauthorized: "Your sign-in session was not ready. Please try again.",
  Forbidden: BLOCKED_CLAIM_COPY.claim_forbidden(null).body,
};

/** Human copy for any error the claim endpoint can return. Never a raw token. */
export function claimErrorMessage(error: unknown): string {
  if (typeof error === "string") {
    if (error in CLAIM_FAILURE_MESSAGES) {
      return CLAIM_FAILURE_MESSAGES[error as ClaimFailureReason];
    }
    if (error in CLAIM_ROUTE_MESSAGES) return CLAIM_ROUTE_MESSAGES[error]!;
  }
  return "We could not save this project to your account. Please try again.";
}
